import React, { useState } from "react";
import { createPortal } from "react-dom";
import Loader from "../../../components/Loader";

const inputClass =
  "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full h-10";

const labelClass = "block text-xs font-semibold text-slate-700 mb-1";

const RestockModal = ({
  show,
  onClose,
  onRestock,
  loading,
  product,
  locations,
}) => {
  const [warehouseId, setWarehouseId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [batchId, setBatchId] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [overwrite, setOverwrite] = useState(false);

  if (!show || !product) return null;

  const totalStock = product.warehouses?.reduce(
    (sum, w) => sum + Number(w.quantity || 0),
    0
  ) || 0;

  const handleRestock = () => {
    if (!warehouseId || !quantity || Number(quantity) <= 0) return;

    onRestock({
      restockEntries: [{ warehouseId, quantity: Number(quantity) }],
      batchId,
      expiryDate,
      overwrite,
    });
  };

  const isValid = warehouseId && quantity && Number(quantity) > 0;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-3">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* HEADER */}
        <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
          <h2 className="text-lg font-semibold">Restock Product</h2>
          <button
            onClick={onClose}
            className="text-xl text-white hover:opacity-80 transition"
          >
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="pb-4 space-y-4 flex-1 overflow-y-auto">
          {/* PRODUCT INFO */}
          <div className="flex gap-4 items-center bg-slate-50 border-y border-slate-200 p-3 px-6 rounded-none">
            {product.thumbnail ? (
              <img
                src={`${process.env.REACT_APP_IMAGE_URL}/${product.thumbnail}`}
                alt={product.name}
                className="w-12 h-12 rounded-lg object-contain bg-white border border-slate-200"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-base border border-blue-200">
                {product.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Product</span>
              <h3 className="text-sm font-bold text-slate-800 truncate">{product.name}</h3>
              <span className="text-[10px] font-semibold text-slate-500 mt-0.5 block">
                Current Total Stock: {totalStock}
              </span>
            </div>
          </div>

          <div className="space-y-3 px-6">
            {/* ROW 1: Warehouse & Quantity */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className={labelClass}>Warehouse</label>
                <select
                  className={inputClass}
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                >
                  <option value="">Select warehouse</option>
                  {locations.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-32">
                <label className={labelClass}>Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className={inputClass}
                  placeholder="Qty"
                />
              </div>
            </div>

            {/* ROW 2: Expiry Date & Batch ID */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className={labelClass}>New Expiry Date (Optional)</label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="flex-1">
                <label className={labelClass}>New Batch ID (Optional)</label>
                <input
                  type="text"
                  value={batchId}
                  onChange={(e) => setBatchId(e.target.value)}
                  className={inputClass}
                  placeholder="Leave blank to keep current"
                />
              </div>
            </div>

            {/* Overwrite Toggle */}
            <div className="flex flex-col gap-1 mt-1">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="overwriteStock"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                  className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-400 cursor-pointer accent-orange-500"
                />
                <label
                  htmlFor="overwriteStock"
                  className="text-xs font-semibold text-slate-700 cursor-pointer select-none"
                >
                  Overwrite current stock (Reset inventory)
                </label>
              </div>
              {overwrite && (
                <p className="text-[10px] text-orange-600 font-normal leading-normal mt-1">
                  ⚠️ Warning: This will clear all existing stock and set it to the quantity entered above.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>

          <button
            onClick={handleRestock}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
            disabled={loading || !isValid}
          >
            {loading ? (
              <Loader text="Restocking...." inline />
            ) : (
              "Restock"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RestockModal;
